// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';

type Props = {
  visible: boolean;
  pending: boolean;
  errorText?: string | null;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Last stop before an irreversible delete, so the destructive action is never the one a stray tap
 * lands on: cancel sits first and stays enabled even while the request is in flight.
 */
export default function DeleteAccountModal({ visible, pending, errorText, onConfirm, onClose }: Props) {
  const { t } = useI18n();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={() => null} testID="delete-account-modal">
          <Text style={styles.title}>{t('profile.deleteAccountConfirmTitle')}</Text>
          <Text style={styles.body}>{t('profile.deleteAccountConfirmBody')}</Text>
          <Text style={styles.body}>{t('profile.deleteAccountConfirmHistory')}</Text>
          {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}
          <View style={styles.actions}>
            <Pressable
              style={styles.cancelButton}
              onPress={onClose}
              testID="delete-account-cancel"
              accessibilityLabel={t('profile.deleteAccountCancel')}
            >
              <Text style={styles.cancelButtonText}>{t('profile.deleteAccountCancel')}</Text>
            </Pressable>
            <Pressable
              style={[styles.confirmButton, pending && styles.confirmButtonDisabled]}
              onPress={onConfirm}
              disabled={pending}
              testID="delete-account-confirm"
              accessibilityLabel={t('profile.deleteAccountConfirm')}
            >
              <Text style={styles.confirmButtonText}>
                {pending ? t('profile.deleteAccountPending') : t('profile.deleteAccountConfirm')}
              </Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: '#07200D',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#7f1d1d',
    padding: 16,
    gap: 12,
  },
  title: {
    color: '#F5F5DC',
    fontSize: 18,
    fontWeight: '700',
  },
  body: {
    color: '#C8C8B0',
    fontSize: 14,
    lineHeight: 20,
  },
  errorText: {
    color: '#E58F8F',
    fontSize: 13,
    fontWeight: '600',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 4,
  },
  cancelButton: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  cancelButtonText: {
    color: '#F5F5DC',
    fontSize: 14,
    fontWeight: '700',
  },
  confirmButton: {
    borderRadius: 12,
    backgroundColor: '#7f1d1d',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  confirmButtonDisabled: {
    opacity: 0.6,
  },
  confirmButtonText: {
    color: '#fecaca',
    fontSize: 14,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
});
